import { useState } from 'react'
import {
  CAPABILITIES, SCOPE_LABELS, ROLES, ROLE_TOOLS, TOOL_LABELS,
  grantString, byScope, canGrant, roleLabels, grantsToRemove,
} from '../../core/caps'
import type { Scope, StaffMember } from '../../core/types'
import { ALLOWED_DOMAIN } from '../../core/firebase'
import {
  setCaps, setActive, setDistrictAdmin, createUser, saveStaffMember, useStaffMember,
  isStudentEmail, seenLabel, toDate, type UserRow,
} from './model'
import { Text } from '../../ui/Field'
import { ErrorState } from '../../ui/States'
import { ShieldAlert, Check } from 'lucide-react'

/**
 * One person's account: who they are, how to reach them, and what they can do.
 *
 * Access (/users) and the phone-book entry (/staffDirectory) are separate
 * collections on purpose — the directory is readable by every staff member and
 * the access list must not be — but they are one person, so they are edited on
 * one page.
 */

export type Actor = { email: string; caps?: string[]; districtAdmin?: boolean; mayEditDirectory: boolean }

export function summarise(r: { caps?: string[]; districtAdmin?: boolean }): string {
  if (r.districtAdmin) return 'District admin (everything)'
  const parts = ROLE_TOOLS
    .map((t) => ({ t, labels: roleLabels(r, t) }))
    .filter((x) => x.labels.length)
    .map((x) => `${TOOL_LABELS[x.t] ?? x.t}: ${x.labels.join(', ')}`)
  return parts.length ? parts.join(' · ') : 'No tools yet'
}

/** NorseTerm lives at the high school, so that is the scope most people want first. */
export const firstScope = (scopes: Scope[]): Scope => (scopes.includes('hs') ? 'hs' : scopes[0] ?? 'district')

export function ScopeSelect({ scopes, scope, setScope }: { scopes: Scope[]; scope: Scope; setScope: (s: Scope) => void }) {
  if (scopes.length < 2) return null
  return (
    <select className="opt__input" value={scope} aria-label="Scope"
      onChange={(e) => setScope(e.target.value as Scope)} style={{ marginBottom: 'var(--s-3)' }}>
      {scopes.map((s) => <option key={s} value={s}>{SCOPE_LABELS[s]}</option>)}
    </select>
  )
}

/**
 * Role ticks for one scope. A role is "on" only when every one of its
 * capabilities is held at exactly this scope — a role held district-wide shows
 * on the District tab, not here, because untick-ing it here could not remove it.
 */
function RolePicker({ held, scope, actor, busy, onChange }: {
  held: string[]
  scope: Scope
  actor: Actor
  busy: boolean
  onChange: (next: string[]) => void
}) {
  const offerable = ROLES.filter((r) => r.caps.every((c) => canGrant(actor, c, scope)))
  if (!offerable.length) return null
  return (
    <div style={{ display: 'grid', gap: 'var(--s-3)' }}>
      {ROLE_TOOLS.map((tool) => {
        const roles = offerable.filter((r) => r.tool === tool)
        if (!roles.length) return null
        return (
          <div key={tool}>
            <div className="opt__label" style={{ marginBottom: 4 }}>{TOOL_LABELS[tool] ?? tool}</div>
            <div style={{ display: 'grid', gap: 4 }}>
              {roles.map((r) => {
                const grants = r.caps.map((c) => grantString(c, scope))
                const on = grants.every((g) => held.includes(g))
                // Office inside Admin: ticked because it is held, but unticking
                // it alone could not remove anything, so say why instead.
                const within = ROLES.find((o) => o !== r && o.tool === r.tool && o.caps.length > r.caps.length
                  && r.caps.every((c) => o.caps.includes(c))
                  && o.caps.every((c) => held.includes(grantString(c, scope))))
                const toggle = () => {
                  const drop = grantsToRemove(held, r, scope)
                  onChange(on
                    ? held.filter((g) => !drop.includes(g))
                    : [...held, ...grants.filter((g) => !held.includes(g))])
                }
                return (
                  <label key={r.id} className="pick" style={{ alignItems: 'flex-start', paddingTop: 'var(--s-2)' }}>
                    <input type="checkbox" checked={on} disabled={busy || !!within} onChange={toggle} style={{ marginTop: 3 }} />
                    <span className="pick__label">
                      <span style={{ fontWeight: 600 }}>{r.label}</span>
                      {within && <span className="opt__hint" style={{ marginLeft: 6 }}>included in {within.label}</span>}
                      <span style={{ display: 'block', fontSize: 'var(--t-xs)', color: 'var(--ink-muted)', marginTop: 2 }}>
                        {r.summary}
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function AddPerson({ scopes, actor, who, existing, onDone }: {
  scopes: Scope[]
  actor: Actor
  who: string
  existing: UserRow[]
  onDone: (id: string | null) => void
}) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [scope, setScope] = useState<Scope>(firstScope(scopes))
  const [caps, setCapsDraft] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const clean = email.trim().toLowerCase()
  const problem =
    !clean ? null
    : !clean.endsWith('@' + ALLOWED_DOMAIN) ? `Use their @${ALLOWED_DOMAIN} address.`
    : isStudentEmail(clean) ? 'That is a student address. Students come from the student list, not from here.'
    : existing.some((r) => r.id === clean) ? 'They already have an account. Find them in the list instead.'
    : null

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    if (problem) return
    setBusy(true); setErr(null)
    try {
      const id = await createUser({ email: clean, displayName: name, buildings: [scope], caps }, who, actor.mayEditDirectory)
      onDone(id)
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false) }
  }

  return (
    <form onSubmit={save}>
      <button type="button" className="backlink" onClick={() => onDone(null)}
        style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0 }}>
        ← Accounts
      </button>
      <h1 className="page-title" style={{ margin: '0 0 var(--s-1)' }}>Add someone</h1>
      <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-sm)', margin: '0 0 var(--s-4)', lineHeight: 1.5 }}>
        They can sign in as soon as you save. No need to wait for them to try first.
      </p>

      {err && <ErrorState body={`Not saved. ${err}`} />}

      <Text label="District email" type="email" value={email} onChange={setEmail} required
        placeholder={`name@${ALLOWED_DOMAIN}`} help={problem ?? undefined} />
      <Text label="Name" value={name} onChange={setName} required />

      <ScopeSelect scopes={scopes} scope={scope} setScope={(s) => { setScope(s); setCapsDraft([]) }} />
      <RolePicker held={caps} scope={scope} actor={actor} busy={busy} onChange={setCapsDraft} />

      <button className="btn" type="submit" disabled={busy || !!problem || !clean || !name.trim()}
        style={{ marginTop: 'var(--s-4)' }}>
        {busy ? 'Saving…' : 'Add them'}
      </button>
      <p className="opt__hint">Individual permissions for exceptions are on their page once they are added.</p>
    </form>
  )
}

export function PersonEditor({ person, scopes, actor, who, onBack }: {
  person: UserRow
  scopes: Scope[]
  actor: Actor
  who: string
  onBack: () => void
}) {
  const [scope, setScope] = useState<Scope>(firstScope(scopes))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<Date | null>(null)

  const isMe = person.id === actor.email
  const held = person.caps ?? []
  // What this admin is allowed to offer here. Anything else is invisible.
  const offerable = CAPABILITIES.filter((c) => canGrant(actor, c.id, scope))
  const created = toDate(person.createdAt)

  const run = async (f: () => Promise<unknown>) => {
    setBusy(true); setErr(null)
    try { await f(); setSavedAt(new Date()) }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }
  const save = (next: string[]) => run(() => setCaps(person.id, next, who))
  const toggle = (capId: string) => {
    const g = grantString(capId, scope)
    return save(held.includes(g) ? held.filter((x) => x !== g) : [...held, g])
  }

  return (
    <>
      <button className="backlink" onClick={onBack} style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0 }}>
        ← Accounts
      </button>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 'var(--t-md)', margin: 0 }}>{person.displayName}</h2>
          {!person.active && <span className="pill pill--serious">Turned off</span>}
          {isMe && <span className="pill">You</span>}
        </div>
        <p style={{ color: 'var(--ink-muted)', fontSize: 'var(--t-sm)', margin: '2px 0 var(--s-3)' }}>
          {person.email}
        </p>
        <p style={{ fontSize: 'var(--t-sm)', margin: '0 0 var(--s-2)' }}>{summarise(person)}</p>
        {!person.districtAdmin && byScope(held).map((g) => (
          <div key={g.scope} style={{ marginBottom: 'var(--s-2)' }}>
            <div className="opt__label">{SCOPE_LABELS[g.scope]}</div>
            <div style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)' }}>
              {g.caps.map((c) => c.label).join(', ')}
            </div>
          </div>
        ))}
        <p className="opt__hint" style={{ marginTop: 'var(--s-3)' }}>
          Last signed in: {seenLabel(person.lastSeenAt)}
          {created && <> · Added {created.toLocaleDateString()}{person.createdBy ? ` by ${person.createdBy}` : ''}</>}
          {person.updatedBy && <> · Last changed by {person.updatedBy}</>}
        </p>
      </div>

      <DirectoryCard email={person.id} fallbackName={person.displayName} mayEdit={actor.mayEditDirectory} who={who} />

      {err && <ErrorState body={`That did not save. ${err}`} />}

      {isMe ? (
        <div className="notice notice--info">
          <ShieldAlert size={15} aria-hidden="true" />
          <div>This is you. Nobody can change their own access, not even a district admin, so every
            permission change has two names on it. Ask another admin.</div>
        </div>
      ) : (
        <>
          {actor.districtAdmin && (
            <DistrictAdminCard person={person} busy={busy}
              onSet={(on) => run(() => setDistrictAdmin(person.id, on, who))} />
          )}

          {person.districtAdmin ? (
            !actor.districtAdmin && (
              <div className="notice notice--info">
                <ShieldAlert size={15} aria-hidden="true" />
                <div>District admin. Holds everything regardless of any list. Only another district admin can change that.</div>
              </div>
            )
          ) : (
            <section className="card card--action">
              <h3 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-2)' }}>Change what they can do</h3>

              <ScopeSelect scopes={scopes} scope={scope} setScope={setScope} />
              <RolePicker held={held} scope={scope} actor={actor} busy={busy} onChange={save} />

              <details style={{ marginTop: 'var(--s-4)' }}>
                <summary style={{ cursor: 'pointer', fontSize: 'var(--t-sm)', fontWeight: 600 }}>
                  Individual permissions, for exceptions
                </summary>
                <div style={{ display: 'grid', gap: 4, marginTop: 'var(--s-2)' }}>
                  {offerable.length === 0 && (
                    <p className="opt__hint">You hold nothing you could hand out in {SCOPE_LABELS[scope]}.</p>
                  )}
                  {offerable.map((c) => {
                    const on = held.includes(grantString(c.id, scope))
                    return (
                      <label key={c.id} className="pick" style={{ alignItems: 'flex-start', paddingTop: 'var(--s-2)' }}>
                        <input type="checkbox" checked={on} disabled={busy} onChange={() => toggle(c.id)}
                          style={{ marginTop: 3 }} />
                        <span className="pick__label">
                          <span style={{ fontWeight: 600 }}>
                            {c.label}
                            {c.sensitive && <span className="pill pill--serious" style={{ marginLeft: 6 }}>student data</span>}
                          </span>
                          <span style={{ display: 'block', fontSize: 'var(--t-xs)', color: 'var(--ink-muted)', marginTop: 2 }}>
                            {c.detail}
                          </span>
                        </span>
                      </label>
                    )
                  })}
                </div>
              </details>

              <div className="opt__bar">
                <button className="btn btn--ghost" disabled={busy}
                  onClick={() => run(() => setActive(person.id, !person.active, who))}>
                  {person.active ? 'Turn off this account' : 'Turn the account back on'}
                </button>
              </div>
              <p className="opt__hint">
                Turning an account off takes effect immediately, in whatever session they have open.
                Their name stays on everything they did.
              </p>
            </section>
          )}
        </>
      )}

      {savedAt && (
        <p className="opt__hint" style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
          <Check size={13} aria-hidden="true" /> Saved at {savedAt.toLocaleTimeString()}
        </p>
      )}
    </>
  )
}

/**
 * The one switch that overrides everything else on the page, so it asks twice
 * and says what it means in plain words.
 */
function DistrictAdminCard({ person, busy, onSet }: {
  person: UserRow
  busy: boolean
  onSet: (on: boolean) => void
}) {
  const [confirming, setConfirming] = useState(false)
  const on = !!person.districtAdmin
  return (
    <section className="card">
      <h3 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-1)' }}>District admin</h3>
      <p className="opt__hint" style={{ margin: '0 0 var(--s-3)' }}>
        {on
          ? `${person.displayName} can see and change everything, including other people’s access and yours.`
          : 'A district admin can see and change everything, including other people’s access, yours too. Only another district admin can undo it.'}
      </p>
      {!confirming ? (
        <button className="btn btn--ghost" disabled={busy} onClick={() => setConfirming(true)}>
          {on ? 'Remove district admin' : 'Make district admin'}
        </button>
      ) : (
        <div className="notice notice--warning" role="alert" style={{ marginBottom: 0 }}>
          <ShieldAlert size={15} aria-hidden="true" />
          <div style={{ flex: 1 }}>
            <strong>{on ? `Remove ${person.displayName} as district admin?` : `Make ${person.displayName} a district admin?`}</strong>
            <div style={{ display: 'flex', gap: 'var(--s-2)', marginTop: 'var(--s-2)' }}>
              <button className="btn" disabled={busy} onClick={() => { onSet(!on); setConfirming(false) }}>
                {on ? 'Yes, remove' : 'Yes, make them district admin'}
              </button>
              <button className="btn btn--ghost" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

/**
 * The phone-book half of the person. Editable by whoever holds admin.staff;
 * everyone else on this page sees it read-only.
 */
function DirectoryCard({ email, fallbackName, mayEdit, who }: {
  email: string
  fallbackName: string
  mayEdit: boolean
  who: string
}) {
  const entry = useStaffMember(email)
  const [draft, setDraft] = useState<StaffMember | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  if (entry === null) return null
  const base: StaffMember = entry ?? {
    email, displayName: fallbackName, title: '', buildings: [], active: true,
  }
  const f = draft ?? base
  const set = <K extends keyof StaffMember>(k: K, v: StaffMember[K]) => setDraft({ ...f, [k]: v })

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    try { await saveStaffMember(f, who); setDraft(null) }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  return (
    <section className="card">
      <h3 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-2)' }}>Directory entry</h3>
      {!entry && <p className="opt__hint" style={{ margin: '0 0 var(--s-3)' }}>Not in the staff directory yet.</p>}
      {mayEdit ? (
        <form onSubmit={save}>
          {err && <ErrorState body={`Not saved. ${err}`} />}
          <Text label="Title" value={f.title ?? ''} onChange={(v) => set('title', v)}
            placeholder="Social Studies · Secretary" />
          <Text label="Phone" type="tel" value={f.phone ?? ''} onChange={(v) => set('phone', v)} />
          <Text label="Extension" value={f.extension ?? ''} onChange={(v) => set('extension', v)} />
          <Text label="Room" value={f.room ?? ''} onChange={(v) => set('room', v)} />
          {draft && (
            <button className="btn" type="submit" disabled={busy}>{busy ? 'Saving…' : entry ? 'Save directory entry' : 'Add to directory'}</button>
          )}
        </form>
      ) : entry ? (
        <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)', margin: 0 }}>
          {[entry.title, entry.phone && `${entry.phone}${entry.extension ? ` ×${entry.extension}` : ''}`, entry.room && `Room ${entry.room}`]
            .filter(Boolean).join(' · ') || 'No details yet.'}
        </p>
      ) : null}
    </section>
  )
}
